import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import OrderDetailsModal from './components/OrderDetailsModal';
import {
  adminOrderService,
  type AdminOrder,
  type OrderFulfillmentStatus,
  type OrderPaymentStatus,
} from '../../services/adminOrderService';
import { getInvoicePdfUrl } from '../../services/adminPaymentLinkService';
import { clientService, type Client } from '../../services/clientService';
import { currencyService } from '../../services/currencyService';
import { getAllServices, type Service } from '../../services/servicesStore';

const PAYMENT_FILTERS: Array<{ value: 'all' | OrderPaymentStatus; label: string }> = [
  { value: 'all', label: 'Tous paiements' },
  { value: 'draft', label: 'Brouillon' },
  { value: 'awaiting_payment', label: 'En attente' },
  { value: 'partially_paid', label: 'Partiel' },
  { value: 'paid', label: 'Paye' },
  { value: 'refunded', label: 'Rembourse' },
  { value: 'canceled', label: 'Annule' },
];

const DELIVERY_FILTERS: Array<{
  value: 'all' | OrderFulfillmentStatus;
  label: string;
}> = [
  { value: 'all', label: 'Toutes livraisons' },
  { value: 'new', label: 'Nouveau' },
  { value: 'scheduled', label: 'Planifie' },
  { value: 'in_progress', label: 'En production' },
  { value: 'delivered', label: 'Livre' },
  { value: 'completed', label: 'Termine' },
  { value: 'on_hold', label: 'En pause' },
  { value: 'canceled', label: 'Annule' },
];

const PAYMENT_LABELS: Record<OrderPaymentStatus, string> = {
  draft: 'Brouillon',
  awaiting_payment: 'Paiement en attente',
  partially_paid: 'Paiement partiel',
  paid: 'Paye',
  refunded: 'Rembourse',
  canceled: 'Annule',
};

const DELIVERY_LABELS: Record<OrderFulfillmentStatus, string> = {
  new: 'Nouveau',
  scheduled: 'Planifie',
  in_progress: 'En production',
  delivered: 'Livre',
  completed: 'Termine',
  on_hold: 'En pause',
  canceled: 'Annule',
};

const PAYMENT_BADGES: Record<OrderPaymentStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  awaiting_payment: 'bg-amber-100 text-amber-700',
  partially_paid: 'bg-orange-100 text-orange-700',
  paid: 'bg-emerald-100 text-emerald-700',
  refunded: 'bg-sky-100 text-sky-700',
  canceled: 'bg-gray-100 text-gray-600',
};

const DELIVERY_BADGES: Record<OrderFulfillmentStatus, string> = {
  new: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-blue-100 text-blue-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-green-100 text-green-700',
  on_hold: 'bg-amber-100 text-amber-700',
  canceled: 'bg-gray-100 text-gray-600',
};

const formatDate = (value?: string) => {
  if (!value) {
    return 'Non renseigne';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Non renseigne';
  }

  return parsed.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const OrderManager = () => {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState<'all' | OrderPaymentStatus>('all');
  const [deliveryFilter, setDeliveryFilter] = useState<'all' | OrderFulfillmentStatus>('all');
  const [selectedOrder, setSelectedOrder] = useState<AdminOrder | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'create'>('view');

  const loadData = async () => {
    setIsLoading(true);

    try {
      const [ordersData, clientsData, servicesData] = await Promise.all([
        adminOrderService.getOrders(true),
        clientService.getClients(true),
        getAllServices(true),
      ]);

      setOrders(ordersData);
      setClients(clientsData);
      setServices(servicesData);
    } catch (error) {
      console.error(error);
      toast.error('Erreur de chargement des commandes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);
  const normalizedQuery = query.trim().toLowerCase();

  const filteredOrders = orders.filter((order) => {
    const matchesPayment =
      paymentFilter === 'all' ? true : order.paymentStatus === paymentFilter;
    const matchesDelivery =
      deliveryFilter === 'all' ? true : order.fulfillmentStatus === deliveryFilter;
    const matchesQuery =
      !normalizedQuery ||
      [
        order.orderNumber,
        order.clientName,
        order.clientEmail,
        order.clientCompany,
        order.serviceName,
        order.quoteNumber,
        order.paymentLinkKey,
        ...order.lineItems.map((lineItem) => lineItem.label),
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedQuery));

    return matchesPayment && matchesDelivery && matchesQuery;
  });

  const metrics = orders.reduce(
    (accumulator, order) => {
      accumulator.total += 1;
      accumulator.revenue += currencyService.convertToDisplayAmount(
        order.amount,
        order.currencyCode,
        displayCountry
      );

      if (order.paymentStatus === 'awaiting_payment') {
        accumulator.awaiting += 1;
      }

      if (order.paymentStatus === 'paid') {
        accumulator.paid += 1;
      }

      if (
        order.fulfillmentStatus === 'delivered' ||
        order.fulfillmentStatus === 'completed'
      ) {
        accumulator.delivered += 1;
      }

      return accumulator;
    },
    {
      total: 0,
      awaiting: 0,
      paid: 0,
      delivered: 0,
      revenue: 0,
    }
  );

  const handleOpenCreate = () => {
    setSelectedOrder(adminOrderService.createEmptyOrder());
    setModalMode('create');
  };

  const handleOpenView = (order: AdminOrder) => {
    setSelectedOrder(order);
    setModalMode('view');
  };

  const handleSave = async (order: AdminOrder) => {
    try {
      await adminOrderService.saveOrder(order);
      await loadData();
      setSelectedOrder(null);
      toast.success(
        modalMode === 'create' ? 'Commande creee avec succes !' : 'Commande mise a jour !'
      );
    } catch (error) {
      console.error(error);
      toast.error('Impossible de sauvegarder cette commande');
    }
  };

  const handleDelete = async (order: AdminOrder) => {
    if (!window.confirm(`Supprimer la commande ${order.orderNumber} ?`)) {
      return;
    }

    try {
      await adminOrderService.removeOrder(order.id);
      await loadData();
      setSelectedOrder(null);
      toast.success('Commande supprimee !');
    } catch (error) {
      console.error(error);
      toast.error('Impossible de supprimer cette commande');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Commandes</h2>
          <p className="mt-2 text-gray-600">
            Centralise les commandes, le suivi de paiement, la production et la
            livraison des dossiers.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Affichage admin actuel: {displayCurrencyCode}. Les montants d origine restent
            conserves par commande.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-neon-orange px-5 py-3 text-white transition-colors hover:bg-[#FF4500]"
        >
          <span className="text-lg leading-none">+</span>
          <span>Nouvelle commande</span>
        </button>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Commandes</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{metrics.total}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Paiements en attente</div>
          <div className="mt-2 text-3xl font-bold text-amber-600">{metrics.awaiting}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Payees</div>
          <div className="mt-2 text-3xl font-bold text-emerald-600">{metrics.paid}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Livrees / terminees</div>
          <div className="mt-2 text-3xl font-bold text-blue-600">{metrics.delivered}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">CA suivi ({displayCurrencyCode})</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {currencyService.formatCurrencyAmount(metrics.revenue, displayCurrencyCode, {
              country: displayCountry,
              display: 'code',
            })}
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_240px]">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Recherche
            </label>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Commande, client, email, devis, lien..."
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Paiement
            </label>
            <select
              value={paymentFilter}
              onChange={(event) =>
                setPaymentFilter(event.target.value as 'all' | OrderPaymentStatus)
              }
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            >
              {PAYMENT_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Livraison
            </label>
            <select
              value={deliveryFilter}
              onChange={(event) =>
                setDeliveryFilter(event.target.value as 'all' | OrderFulfillmentStatus)
              }
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            >
              {DELIVERY_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-gray-600">Chargement des commandes...</div>
      ) : filteredOrders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
          <h3 className="text-2xl font-semibold text-gray-900">
            Aucune commande pour le moment
          </h3>
          <p className="mx-auto mt-3 max-w-2xl text-gray-600">
            Cree ta premiere commande pour suivre le paiement, la production, la
            livraison et les references client dans un seul endroit.
          </p>
          <button
            onClick={handleOpenCreate}
            className="mt-8 rounded-lg bg-neon-orange px-5 py-3 font-medium text-white transition-colors hover:bg-[#FF4500]"
          >
            Creer une commande
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => {
            const visibleLineItems = order.lineItems.filter(
              (lineItem) => lineItem.label.trim().length > 0 || lineItem.lineTotal > 0
            );
            const primaryServiceLabel =
              order.serviceName ||
              visibleLineItems.find((lineItem) => lineItem.label.trim().length > 0)?.label ||
              'Non renseigne';
            const displayAmount = currencyService.formatAmountForDisplay(
              order.amount,
              order.currencyCode,
              {
                country: displayCountry,
                display: 'code',
              }
            );
            const originalAmount = currencyService.formatCurrencyAmount(
              order.amount,
              order.currencyCode,
              { display: 'code' }
            );

            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {order.orderNumber}
                      </h3>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          PAYMENT_BADGES[order.paymentStatus]
                        }`}
                      >
                        {PAYMENT_LABELS[order.paymentStatus]}
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          DELIVERY_BADGES[order.fulfillmentStatus]
                        }`}
                      >
                        {DELIVERY_LABELS[order.fulfillmentStatus]}
                      </span>
                    </div>

                    <div className="grid gap-3 text-sm text-gray-600 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Client
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          {order.clientName}
                        </div>
                        <div>{order.clientEmail}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Service
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          {primaryServiceLabel}
                        </div>
                        <div>
                          {visibleLineItems.length > 1
                            ? `+ ${visibleLineItems.length - 1} autre(s) service(s)`
                            : order.clientCompany || 'Sans entreprise'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Dates
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          Commande: {formatDate(order.orderedAt)}
                        </div>
                        <div>Livraison: {formatDate(order.deliveryDate)}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Montant
                        </div>
                        <div className="mt-1 text-xl font-semibold text-neon-orange">
                          {displayAmount}
                        </div>
                        <div className="text-xs text-gray-500">Base: {originalAmount}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {order.paymentLinkKey && (
                      <a
                        href={getInvoicePdfUrl(order.paymentLinkKey)}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg bg-blue-100 px-4 py-2 font-medium text-blue-700 transition-colors hover:bg-blue-200"
                      >
                        Facture PDF
                      </a>
                    )}
                    <button
                      onClick={() => handleOpenView(order)}
                      className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                    >
                      Ouvrir
                    </button>
                    <button
                      onClick={() => handleDelete(order)}
                      className="rounded-lg bg-red-50 px-4 py-2 font-medium text-red-600 transition-colors hover:bg-red-100"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          clients={clients}
          services={services}
          defaultMode={modalMode}
          onClose={() => setSelectedOrder(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default OrderManager;
